/**
 * DTOs for Sales > Lead Finder (PD-16825).
 *
 * The list reads `workd_mission_control.lm_lead_location` — one row per
 * physical pharmacy location built nightly from STATE-BOARD registrations —
 * and decides per tenant, from the tenant's own clients and leads only,
 * whether each location is already known. There is no TypeORM entity for the
 * mission-control table, so location fields are declared here.
 *
 * DEA data is never part of these shapes (Chris, 2026-10-04): a DEA business
 * activity may have decided a classification, and the row then says only
 * "Workd classification" as its basis.
 */

/** Class-of-trade codes (v1: pharmacies). */
export const LEAD_FINDER_CLASSES_OF_TRADE = [
	'RETAIL',
	'LONG_TERM_CARE',
	'HOSPITAL_INSTITUTIONAL',
	'CLINIC',
	'COMPOUNDING',
	'SPECIALTY',
	'HOME_INFUSION',
	'MAIL_ORDER',
	'NUCLEAR',
	'UNKNOWN',
] as const;
export type LeadFinderClassOfTrade = (typeof LEAD_FINDER_CLASSES_OF_TRADE)[number];

export const LEAD_FINDER_CLASS_OF_TRADE_LABELS: Record<LeadFinderClassOfTrade, string> = {
	RETAIL: 'Retail / community',
	LONG_TERM_CARE: 'Long-term care',
	HOSPITAL_INSTITUTIONAL: 'Hospital / institutional',
	CLINIC: 'Clinic',
	COMPOUNDING: 'Compounding',
	SPECIALTY: 'Specialty',
	HOME_INFUSION: 'Home infusion',
	MAIL_ORDER: 'Mail order',
	NUCLEAR: 'Nuclear',
	UNKNOWN: 'Unknown',
};

/** Ownership codes. UNKNOWN is shown as such — it is never defaulted to independent. */
export const LEAD_FINDER_OWNERSHIPS = ['INDEPENDENT', 'CHAIN', 'INSTITUTIONAL', 'UNKNOWN'] as const;
export type LeadFinderOwnership = (typeof LEAD_FINDER_OWNERSHIPS)[number];

export const LEAD_FINDER_OWNERSHIP_LABELS: Record<LeadFinderOwnership, string> = {
	INDEPENDENT: 'Independent',
	CHAIN: 'Chain',
	INSTITUTIONAL: 'Institutional',
	UNKNOWN: 'Unknown',
};

/**
 * Whether a location is already known to this tenant:
 * - `new` — nothing of the tenant's matches it;
 * - `possible` — a client or lead shares its address, phone, NPI or name and ZIP;
 * - `existing` — one of its licenses is already on, or matched to, a client.
 */
export type LeadFinderLeadStatus = 'new' | 'possible' | 'existing';

/**
 * The "Lead status" filter: `any` shows every location with its status. (Not
 * `all`: the list framework drops a radio value of 'all' before sending, which
 * would silently turn "All" back into the default "New".) Every value but
 * `dismissed` leaves out the locations this tenant dismissed as not a fit;
 * `dismissed` shows only those (the Dismissed tab).
 */
export type LeadFinderLeadStatusFilter = 'new' | 'possible' | 'any' | 'dismissed';

/** "Issued within" choices, in days. */
export const LEAD_FINDER_ISSUED_WITHIN_DAYS = [7, 30, 90, 180, 365] as const;

export interface LeadFinderFiltersModel {
	leadFinderKeywords?: string;
	leadFinderLeadStatus?: LeadFinderLeadStatusFilter;
	leadFinderClassOfTrade?: Array<LeadFinderClassOfTrade>;
	leadFinderOwnership?: Array<LeadFinderOwnership>;
	leadFinderState?: Array<string>;
	leadFinderCity?: string;
	leadFinderZip?: string;
	/** Days, as a string from the select (one of LEAD_FINDER_ISSUED_WITHIN_DAYS). */
	leadFinderIssuedWithin?: string;
}

export interface LeadFinderLocationModel {
	/** lm_lead_location.location_key — stable across nightly rebuilds while the place is unchanged. */
	id: string;
	name: string | null;
	/** The legal name when it differs from the storefront (DBA) name. */
	legalName: string | null;
	address1: string | null;
	address2: string | null;
	city: string | null;
	state: string;
	zip: string | null;
	/** One-line address for the list's first column; '' when there is none. */
	addressFull: string;
	/** 'Board' when the board published no street address and the NPI record supplied it. */
	addressSource: 'Board' | 'NPI';
	phone: string | null;
	phoneSource: 'NPI' | 'Board' | null;
	npiNumber: string | null;
	classOfTrade: LeadFinderClassOfTrade;
	classOfTradeLabel: string;
	/** Where the class came from, in words ("State permit type", "NPI taxonomy", "Workd classification", …). */
	classBasisLabel: string;
	ownership: LeadFinderOwnership;
	ownershipLabel: string;
	ownershipBasisLabel: string;
	/** Latest issue date across the state licenses at the location (YYYY-MM-DD). */
	newestIssueDate: string | null;
	/** False for boards that publish no issue dates at all (a missing date there is not "old"). */
	issueDatePublished: boolean;
	/** When Workd first saw any license at the location (YYYY-MM-DD). */
	foundDate: string | null;
	licenseCount: number;
	primaryLicenseNumber: string | null;
	primaryLicenseType: string | null;
	leadStatus: LeadFinderLeadStatus;
	leadStatusLabel: string;
	/** The tenant client a `possible` / `existing` status points at (first hit). */
	matchedClientId: number | null;
	matchedClientName: string | null;
	/** "address", "phone", "NPI", "name and ZIP", "license". */
	matchedReason: string | null;
	/** Exact (license-level) matches can never be imported again. */
	canImport: boolean;
	/** This tenant dismissed the location as not a fit (it leaves the results until restored). */
	dismissed: boolean;
	/** "Not a fit", with the reason when one was given; null when not dismissed. */
	dismissedNote: string | null;
}

export interface LeadFinderListResponseModel {
	success: true;
	data: Array<LeadFinderLocationModel>;
	pagination: { page: number; limit: number; total: number; totalPages: number };
}

/** Most locations one import may carry (single or bulk). */
export const LEAD_FINDER_IMPORT_MAX = 500;

/** Lead source written on every lead the Lead Finder creates. */
export const LEAD_FINDER_LEAD_SOURCE = 'Workd Lead Finder';

/** creationSource written on every lead the Lead Finder creates. */
export const LEAD_FINDER_CREATION_SOURCE = 'lead_finder';

export interface LeadFinderImportRequestModel {
	/** lm_lead_location keys (row ids), 1 to LEAD_FINDER_IMPORT_MAX. */
	locationKeys: Array<string>;
	/** Sales rep for the new leads; null leaves them unassigned. */
	salespersonUserId: number | null;
	/** Company the leads belong to; null uses the importing user's default. */
	companyId: number | null;
	/** Optional static client list (for a campaign audience) to add the new leads to. */
	clientListId: number | null;
}

export interface LeadFinderImportResponseModel {
	/** The import run carrying the rows; its page shows created / held / skipped. */
	importRunId: number;
	queued: number;
	/** Locations left out because one of their licenses is already on a client. */
	skippedExisting: number;
	/** Locations left out because they were not found (rebuilt away overnight). */
	skippedMissing: number;
}

/** Longest dismissal reason accepted. */
export const LEAD_FINDER_DISMISS_REASON_MAX = 255;

export interface LeadFinderDismissRequestModel {
	/** lm_lead_location keys (row ids), 1 to LEAD_FINDER_IMPORT_MAX. */
	locationKeys: Array<string>;
	/** Optional note on why the locations are not a fit. */
	reason?: string | null;
}

export interface LeadFinderDismissResponseModel {
	dismissed: number;
	/** Already dismissed before this request. */
	alreadyDismissed: number;
	/** Not listed by the Lead Finder (rebuilt away overnight, or never a location). */
	skippedMissing: number;
}

export interface LeadFinderRestoreRequestModel {
	locationKeys: Array<string>;
}

export interface LeadFinderRestoreResponseModel {
	restored: number;
}
